import React, { useState } from 'react';
import I18n from 'i18n-js';
import { tv } from 'tailwind-variants';
import { TCITooltip } from '@/components/ui/Tooltip';
import Text from '@/components/ui/Text';
import useFlags from '@/hooks/useFlags';
import useLessonOverviewContext from '@/components/shared/NewLessonOverview/useLessonOverviewContext';
import AsyncAssignmentModal from '@/components/staffer/AssignmentModal/AsyncAssignmentModal';
import { useModalManager } from '@/components/common/Modal';
import { diveDeeperFormat } from '@/components/shared/NewLessonOverview/DiveDeeper/DiveDeeper.utils';
import ReadingCard from './ReadingCard';
import ReferenceMaterialCard from './ReferenceMaterialCard';

const containerStyles = tv({
  base: 'tw-bg-[#F6F5FA] tw-overflow-y-auto tw-pb-4 tw-flex-grow-[100] tw-min-w-0',
  variants: {
    reflow: {
      false: 'tw-overflow-x-hidden tw-px-[2rem]',
      true: 'tw-px-4 reflow-narrow:tw-px-8',
    },
  },
});

const gridStyles = tv({
  base: 'tw-grid tw-gap-4 hd:tw-grid-cols-2',
  variants: {
    reflow: {
      false: 'tw-grid-cols-[repeat(auto-fit,minmax(24rem,1fr))]',
      true: 'tw-grid-cols-[repeat(auto-fit,minmax(min(24rem,100%),1fr))]',
    },
  },
});

const DiveDeeper = () => {
  const { accessibilityReflow } = useFlags();
  const lesson = useLessonOverviewContext(state => state.lesson);
  const sections = useLessonOverviewContext(state => state.sections);
  const programId = useLessonOverviewContext(state => state.programId);
  const stafferId = useLessonOverviewContext(state => state.stafferId);
  const isStaffer = useLessonOverviewContext(state => state.isStaffer);
  const setLesson = useLessonOverviewContext(state => state.setLesson);
  const isLoading = useLessonOverviewContext(state => state.isLoading);
  const modalManager = useModalManager();
  const [initialValues, setInitialValues] = useState({});

  if (!lesson || !sections) return null;

  const {
    readings = [],
    referenceMaterialAssignments = [],
    taggedReferenceMaterials = [],
  } = diveDeeperFormat(lesson, sections);

  const hasDiveDeeperContent = () => (
    !(readings.length === 0 && referenceMaterialAssignments.length === 0 && taggedReferenceMaterials?.length === 0)
  );

  if (!hasDiveDeeperContent()) return null;

  const handleAssignmentModalSubmit = (assignment) => {
    const createdRefMaterialIndex = taggedReferenceMaterials.findIndex(ref_material => (
      ref_material.id === assignment.assignment_models_hash['ReferenceMaterial']?.[0]?.id));

    const createdRefMaterial = taggedReferenceMaterials[createdRefMaterialIndex];
    const taggedRefMaterials = taggedReferenceMaterials.splice(createdRefMaterialIndex, 1);

    const lessonWithNewRefMaterials = { ...lesson };
    lessonWithNewRefMaterials.taggedReferenceMaterials = taggedRefMaterials;
    createdRefMaterial.isAssigned = true;
    createdRefMaterial.label = assignment.assignment_models_hash['ReferenceMaterial']?.[0]?.label;
    lessonWithNewRefMaterials.referenceMaterialAssignments.push(createdRefMaterial);
    setLesson(lesson);

    modalManager.close();
  };

  return (
    <div className={containerStyles({ reflow: accessibilityReflow })}>
      <div className="tw-flex tw-items-center tw-mt-[1.5rem] tw-mb-[2rem] tw-flex-basis-auto">
        <Text className="tw-mr-[0.75rem]" tag="h2" variant="heading3"> {I18n.t('dive_deeper_with')} </Text>
        {isStaffer && (
          <TCITooltip>
            <TCITooltip.Trigger asChild>
              <span>
                <i aria-hidden="true" className="tw-text-[1.5rem] fa fa-info-circle fa-lg" />
              </span>
            </TCITooltip.Trigger>
            <TCITooltip.Content>
              {I18n.t('lesson_subheader_dive_deeper')}
            </TCITooltip.Content>
          </TCITooltip>
        )}
      </div>
      <div className={gridStyles({ reflow: accessibilityReflow })}>
        {readings?.map(reading => (
          <ReadingCard
            key={`reading-${reading.id}`}
            openAssign={modalManager.open}
            reading={reading}
            setInitialValues={setInitialValues}
          />
        ))}
        {referenceMaterialAssignments?.map(referenceMaterial => (
          <ReferenceMaterialCard
            key={`referenceMaterial-${referenceMaterial.id}`}
            openAssign={modalManager.open}
            referenceMaterial={referenceMaterial}
            setInitialValues={setInitialValues}
          />
        ))}
        {isStaffer && taggedReferenceMaterials.map(referenceMaterial => (
          <ReferenceMaterialCard
            key={`taggedReferenceMaterial-${referenceMaterial.id}`}
            openAssign={modalManager.open}
            referenceMaterial={referenceMaterial}
            setInitialValues={setInitialValues}
          />
        ))}
      </div>
      {(!isLoading && (sections || taggedReferenceMaterials)) && (
        <AsyncAssignmentModal
          closeModal={modalManager.close}
          handlePostSave={handleAssignmentModalSubmit}
          initialLessonId={lesson.id}
          initialValues={initialValues}
          modalIsOpen={modalManager.isOpen}
          programId={programId}
          stafferId={stafferId}
        />
      )}
    </div>
  );
};

export default DiveDeeper;
